import React from 'react'

import { EuiPanel, EuiTitle, EuiSpacer, EuiLoadingContent, EuiEmptyPrompt } from '@elastic/eui'

import { ReportCampaign } from 'api/interfaces'
import ReportDataGrid, { ReportDataGridColumn, ReportDataGridSorting } from 'features/reports/ReportDataGrid'
import { filterCampaignDataByViews } from 'features/reports/campaign-reports/reportUtils'
import { formatCurrency, formatNumber, formatPercentage } from 'utils/Formatters'

import { EmptyPromptComponent, LoadingComponent, TitleComponent } from './FormattingComponents'

interface CampaignPerformanceProps {
  results?: ReportCampaign[]
  isLoading: boolean
}

const CampaignPerformance: React.FC<CampaignPerformanceProps> = ({ results, isLoading }) => {
  const title = 'Campaign Data'

  if (isLoading) {
    return <LoadingComponent title={title} />
  }

  const filteredResults = filterCampaignDataByViews(results)

  if (filteredResults.length === 0) {
    return <EmptyPromptComponent title={title} body='There is no campaign data for the selected period.' />
  }

  const isInternetCampaign = filteredResults.some(result => result.campaignType === 'Internet')

  const defaultSorting = [
    {
      id: 'forDate',
      direction: 'desc'
    }
  ] as ReportDataGridSorting[]

  const columns: ReportDataGridColumn[] = [
    {
      id: 'forDate',
      displayAsText: 'Date',
      schema: 'date'
    },
    {
      id: 'views',
      displayAsText: 'Views',
      schema: 'numeric',
      render: (value: number) => formatNumber(value, 0, 0)
    }
  ]

  if (isInternetCampaign) {
    columns.push(
      {
        id: 'clicks',
        displayAsText: 'Clicks',
        schema: 'numeric',
        render: (value: number) => formatNumber(value, 0, 0)
      },
      {
        id: 'ctr',
        displayAsText: 'CTR %',
        schema: 'numeric',
        render: (value: number) => formatPercentage(value / 100, 3, 3)
      }
    )
  }

  columns.push(
    {
      id: 'spend',
      displayAsText: 'Spend',
      schema: 'currency',
      render: (value: number) => formatCurrency(value)
    },
    {
      id: 'cpm',
      displayAsText: 'CPM',
      schema: 'currency',
      render: (value: number) => formatCurrency(value)
    }
  )

  return (
    <EuiPanel>
      <TitleComponent title={title} />

      <EuiSpacer size='m' />

      <ReportDataGrid data={filteredResults} columns={columns} defaultSorting={defaultSorting} />
    </EuiPanel>
  )
}

export default CampaignPerformance
